'use client'

import {
  CodeDeliveryDetails,
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  ConfirmSignUpOutput,
  resendSignUpCode,
  resetPassword as resetPasswordFn,
  ResetPasswordOutput,
  signIn,
  SignInInput,
  SignInOutput,
  signUp,
  SignUpOutput,
} from 'aws-amplify/auth'

import { isCognitoSessionRecoveryError } from '@/services/auth/cognitoErrors.client'
import { clearRecoverableClientAuthArtifacts } from '@/services/auth/session.client'
import { writeLastActivity } from '@/services/auth/inactivity.client'

type LoginResult =
  | { status: 'success'; data: SignInOutput }
  | { status: 'error'; error: unknown }

type AuthResult<T> =
  | { status: 'success'; data: T }
  | { status: 'error'; error: unknown }

function toErrorResult(error: unknown) {
  return { status: 'error' as const, error }
}

function toLoginSuccessResult(data: SignInOutput): LoginResult {
  if (data.isSignedIn) {
    writeLastActivity(Date.now())
  }
  return { status: 'success', data }
}

async function signInWithSessionRecovery(
  input: SignInInput,
): Promise<LoginResult> {
  try {
    const result = await signIn(input)
    return toLoginSuccessResult(result)
  } catch (error) {
    if (!isCognitoSessionRecoveryError(error)) {
      return toErrorResult(error)
    }

    clearRecoverableClientAuthArtifacts()

    try {
      const retriedResult = await signIn(input)
      return toLoginSuccessResult(retriedResult)
    } catch (retryError) {
      return toErrorResult(retryError)
    }
  }
}

type SignUpParameters = {
  password: string
  email: string
  name: string
  locale: string
}

export const createUser = async ({
  email,
  password,
  name,
  locale = 'fr',
}: SignUpParameters): Promise<AuthResult<SignUpOutput>> => {
  try {
    const result = await signUp({
      username: email,
      password: password,
      options: {
        userAttributes: {
          name: name,
          'custom:source': 'manage',
        },
        clientMetadata: {
          locale: locale,
        },
        autoSignIn: true,
      },
    })
    return { status: 'success', data: result }
  } catch (error) {
    return toErrorResult(error)
  }
}

export const resetPassword = async ({
  email,
  locale = 'fr',
}: {
  email: string
  locale?: string
}): Promise<AuthResult<ResetPasswordOutput>> => {
  try {
    const result = await resetPasswordFn({
      username: email.trim(),
      options: { clientMetadata: { locale: locale } },
    })
    return { status: 'success', data: result }
  } catch (error) {
    return toErrorResult(error)
  }
}

export const createNewPassword = async ({
  username,
  code,
  password,
}: {
  username: string
  code: string
  password: string
}): Promise<AuthResult<void>> => {
  try {
    const result = await confirmResetPassword({
      username: username.trim(),
      confirmationCode: code,
      newPassword: password,
    })
    return { status: 'success', data: result }
  } catch (error) {
    return toErrorResult(error)
  }
}

export const resendConfirmationCode = async (
  username: string,
): Promise<AuthResult<CodeDeliveryDetails>> => {
  try {
    const result = await resendSignUpCode({ username: username.trim() })
    return { status: 'success', data: result }
  } catch (error) {
    return toErrorResult(error)
  }
}

export const confirmUser = async ({
  email,
  oneTimeCode,
}: {
  email: string
  oneTimeCode: string
}): Promise<AuthResult<ConfirmSignUpOutput>> => {
  try {
    const result = await confirmSignUp({
      username: email.trim(),
      confirmationCode: oneTimeCode,
    })
    return { status: 'success', data: result }
  } catch (error) {
    return toErrorResult(error)
  }
}

export const loginUser = async ({
  username,
  password,
}: SignInInput): Promise<LoginResult> => {
  return signInWithSessionRecovery({ username, password })
}

export const confirmSignInWithNewPassword = async ({
  newPassword,
}: {
  newPassword: string
}): Promise<LoginResult> => {
  try {
    const result = await confirmSignIn({ challengeResponse: newPassword })
    return toLoginSuccessResult(result)
  } catch (error) {
    return toErrorResult(error)
  }
}
